import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

interface FlowField {
    label: string;
    key: string;
    status?: string;
}

interface FlowNode {
    nodeLabel: string;
    rootFields: FlowField[];
}

interface SessionMetadata {
    completeness?: { nodes?: FlowNode[] };
    collected_fields?: Record<string, any>;
    created_at?: string;
    completed_at?: string;
    session_status?: string;
    escalation_reason?: string;
    [key: string]: any;
}

@Component({
    selector: 'app-case-agent-flow-session-data',
    templateUrl: './case-agent-flow-session-data.component.html',
    styleUrls: ['./case-agent-flow-session-data.component.scss'],
    standalone: false
})
export class CaseAgentFlowSessionDataComponent implements OnInit {
    public metadata: SessionMetadata | null = null;
    public nodes: FlowNode[] = [];
    public collectedFields: Record<string, any> = {};
    public hasData = false;

    public generalInfo: { label: string; value: any }[] = [];

    constructor(
        private config: DynamicDialogConfig,
        private dynamicDialogRef: DynamicDialogRef
    ) {}

    ngOnInit() {
        const raw = this.config.data?.metadata;
        if (!raw) {
            this.hasData = false;
            return;
        }

        try {
            this.metadata = typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch {
            this.metadata = null;
        }

        if (!this.metadata) {
            this.hasData = false;
            return;
        }

        this.hasData = true;
        //desde S3 metadata es un sobre { recopilacion, informe, ... }; la recopilación (que trae
        //completeness y collected_fields) puede venir anidada. Retrocompatible con el formato antiguo.
        const src: SessionMetadata = this.metadata['recopilacion'] ?? this.metadata;
        this.nodes = src.completeness?.nodes ?? [];
        this.collectedFields = src.collected_fields ?? {};

        this.generalInfo = [
            { label: 'Creado', value: this.formatDate(src.created_at) },
            { label: 'Completado', value: this.formatDate(src.completed_at) },
            { label: 'Estado', value: src.session_status ?? '-' },
            { label: 'Motivo de escalado', value: src.escalation_reason ?? '-' }
        ];
    }

    public getFieldValue(key: string): any {
        if (!key) return null;
        // Allow dotted keys
        return key.split('.').reduce((acc: any, part) => (acc != null ? acc[part] : undefined), this.collectedFields);
    }

    public isCollected(field: FlowField): boolean {
        return field.status === 'collected';
    }

    public statusLabel(field: FlowField): { icon: string; text: string } {
        switch (field.status) {
            case 'missing':
                return { icon: 'pi pi-minus-circle', text: 'No recogido' };
            case 'skipped':
                return { icon: 'pi pi-forward', text: 'Omitido' };
            case 'not_applicable':
                return { icon: 'pi pi-ban', text: 'No aplica' };
            default:
                return { icon: 'pi pi-question-circle', text: field.status ?? '-' };
        }
    }

    public displayValue(value: any): string {
        if (value === null || value === undefined || value === '') return '-';
        if (Array.isArray(value)) {
            return value.length === 0 ? '-' : value.map(v => this.displayValue(v)).join(', ');
        }
        if (typeof value === 'object') return JSON.stringify(value);
        if (typeof value === 'boolean') return value ? 'Sí' : 'No';
        return String(value);
    }

    private formatDate(value?: string): string {
        if (!value) return '-';
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleString();
    }

    close() {
        this.dynamicDialogRef.close();
    }
}
